// ---------------------------------------------------------------------------
// AdminActionsPane — aba "Ações" do grupo ADM no InputDrawerV3 (leva 97).
// ---------------------------------------------------------------------------
// Concentra operações destrutivas / administrativas que antes viviam
// espalhadas no toolbar (Reprocessar) ou simplesmente não existiam
// (Excluir envio do mês). Visibilidade da aba é controlada pelo
// ``InputDrawerV3`` — só empurra o item para o rail quando
// ``userRole === "DEV"``. Mesmo gate vive no backend
// (``@requires('dev_only')`` em ``/reprocess/`` e ``/delete-upload/``).
//
// Padrão visual de cada ação:
//   - Card com título, descrição curta e botão de execução.
//   - Confirmação inline (``confirm``) para destrutivos. Aqui não
//     usamos modal próprio para manter o pane leve — uma confirmação
//     nativa basta para DEV (gate de público restrito).
//   - Feedback inline: estado de loading, último resultado (sucesso
//     com sumário das contagens, ou erro com código + mensagem).
//
// Cada ação só fica habilitada quando faz sentido (ex.: "Excluir
// envio" exige ``hasDelivered``; "Reprocessar" também). Quando
// desabilitada, o card mostra o motivo no lugar do botão.
// ---------------------------------------------------------------------------

import React, { useState } from "react";
import PropTypes from "prop-types";
import { ThreeDots } from "react-loader-spinner";
import { Trans, useTranslation } from "react-i18next";

import DeleteSweep from "@material-ui/icons/DeleteSweep";
import Cached from "@material-ui/icons/Cached";

import useDrawerStyles from "../styles";
import useThemeColors from "../../../../../hooks/useThemeColors";
import useNativeWheelScroll from "../../hooks/useNativeWheelScroll";
import { useOverlayScrollStyles } from "../../styles";
import {
  deleteInputUploadV3,
  reprocessInputV3,
} from "../../../../../services/inputsV3Service";

const ActionCard = ({
  icon: Icon,
  title,
  description,
  buttonLabel,
  onConfirm,
  confirmMessage,
  disabled,
  disabledReason,
  busy,
  result,
  classes,
}) => {
  const handleClick = () => {
    if (disabled || busy) return;
    // Confirmação nativa — gate de DEV ja restringe acesso; modal
    // próprio adicionaria peso visual desproporcional.
    // eslint-disable-next-line no-alert
    const ok = window.confirm(confirmMessage);
    if (!ok) return;
    onConfirm();
  };

  return (
    <div className={classes.admActionCard}>
      <div className={classes.admActionCardHeader}>
        <Icon className={classes.admActionCardIcon} />
        <div className={classes.admActionCardTitle}>{title}</div>
      </div>
      <div className={classes.admActionCardDescription}>{description}</div>
      <div className={classes.admActionCardFooter}>
        {disabled ? (
          <span className={classes.admActionCardDisabledHint}>
            {disabledReason}
          </span>
        ) : (
          <button
            type="button"
            className={`${classes.btn} ${classes.btnDanger}`}
            onClick={handleClick}
            disabled={busy}
          >
            {busy ? (
              <>
                <ThreeDots
                  color="#fff"
                  height="14px"
                  width="28px"
                  wrapperStyle={{ height: "14px", width: "28px" }}
                />
                {buttonLabel}
              </>
            ) : (
              <>
                <Icon />
                {buttonLabel}
              </>
            )}
          </button>
        )}
      </div>
      {result && (
        <div
          className={
            result.ok ? classes.admActionResultOk : classes.admActionResultErr
          }
          role="status"
        >
          {result.message}
        </div>
      )}
    </div>
  );
};

ActionCard.propTypes = {
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.node.isRequired,
  buttonLabel: PropTypes.string.isRequired,
  onConfirm: PropTypes.func.isRequired,
  confirmMessage: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  disabledReason: PropTypes.string,
  busy: PropTypes.bool,
  result: PropTypes.shape({
    ok: PropTypes.bool,
    message: PropTypes.string,
  }),
  classes: PropTypes.object.isRequired,
};

const AdminActionsPane = ({
  input,
  appName,
  year,
  month,
  hasDelivered,
  userRole,
  onReprocess,
}) => {
  const { t } = useTranslation();
  const { clientColor } = useThemeColors();
  const classes = useDrawerStyles({ clientColor });
  const overlayScrollClasses = useOverlayScrollStyles();
  const wheelRef = useNativeWheelScroll();

  // Gate defensivo: o ``InputDrawerV3`` ja so empurra esta aba para
  // DEV, mas se algum estado forcado tentar montar o pane fora desse
  // caminho, recusamos renderizar (e nao expomos os botoes / nao
  // chamamos as APIs destrutivas).
  if (userRole !== "DEV") return null;

  const metaName = input?.meta_name || input?.input_id || null;

  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null);
  const [reprocessBusy, setReprocessBusy] = useState(false);
  const [reprocessResult, setReprocessResult] = useState(null);

  const formatSummary = (summary) => {
    if (!summary) return null;
    const parts = [];
    if (summary.str_notes_deleted) {
      parts.push(
        t("modeling_inputs_v3.admin_actions_pane.summary_str_notes", {
          count: summary.str_notes_deleted,
        }),
      );
    }
    if (summary.csl_notes_deleted) {
      parts.push(
        t("modeling_inputs_v3.admin_actions_pane.summary_csl_notes", {
          count: summary.csl_notes_deleted,
        }),
      );
    }
    if (summary.decisions_superseded) {
      parts.push(
        t("modeling_inputs_v3.admin_actions_pane.summary_decisions", {
          count: summary.decisions_superseded,
        }),
      );
    }
    if (summary.xlinks_reset) {
      parts.push(
        t("modeling_inputs_v3.admin_actions_pane.summary_xlinks", {
          count: summary.xlinks_reset,
        }),
      );
    }
    if (summary.blocks_marked_stale) {
      parts.push(
        t("modeling_inputs_v3.admin_actions_pane.summary_blocks", {
          count: summary.blocks_marked_stale,
        }),
      );
    }
    return parts.length > 0 ? parts.join(" • ") : null;
  };

  const handleDelete = async () => {
    if (!metaName || !appName || !year || !month) return;
    setDeleteBusy(true);
    setDeleteResult(null);
    try {
      const res = await deleteInputUploadV3({
        appName,
        year,
        month,
        metaName,
      });
      const summary = formatSummary(res?.summary);
      setDeleteResult({
        ok: true,
        message:
          t("modeling_inputs_v3.admin_actions_pane.delete_success") +
          (summary ? ` ${summary}.` : ""),
      });
    } catch (err) {
      const code = err?.response?.data?.error_code || err?.message || "erro";
      setDeleteResult({
        ok: false,
        message: t("modeling_inputs_v3.admin_actions_pane.delete_error", {
          code,
        }),
      });
    } finally {
      setDeleteBusy(false);
    }
  };

  const handleReprocess = async () => {
    if (!metaName || !appName || !year || !month) return;
    setReprocessBusy(true);
    setReprocessResult(null);
    try {
      // Se o parent expos ``onReprocess`` (usado pelo toolbar antigo),
      // delegamos para reaproveitar o pipeline de feedback dele (toast,
      // refresh de runtime, etc.). Senão, chamamos o service direto.
      if (typeof onReprocess === "function") {
        await onReprocess();
        setReprocessResult({
          ok: true,
          message: t("modeling_inputs_v3.admin_actions_pane.reprocess_success"),
        });
      } else {
        const res = await reprocessInputV3({ appName, year, month, metaName });
        const blocks = res?.cascade?.blocks_blocked || 0;
        const xlinks = res?.cascade?.xlinks_reset || 0;
        const notes = res?.cascade?.csl_notes_deleted || 0;
        const notesSuffix = notes
          ? t("modeling_inputs_v3.admin_actions_pane.reprocess_notes_detail", {
              notes,
            })
          : "";
        setReprocessResult({
          ok: true,
          message:
            t("modeling_inputs_v3.admin_actions_pane.reprocess_success") +
            (blocks || xlinks
              ? " " +
                t(
                  "modeling_inputs_v3.admin_actions_pane.reprocess_success_detail",
                  {
                    blocks,
                    xlinks,
                    notes: notesSuffix,
                  },
                )
              : ""),
        });
      }
    } catch (err) {
      const code = err?.response?.data?.error_code || err?.message || "erro";
      setReprocessResult({
        ok: false,
        message: t("modeling_inputs_v3.admin_actions_pane.reprocess_error", {
          code,
        }),
      });
    } finally {
      setReprocessBusy(false);
    }
  };

  return (
    <div
      ref={wheelRef}
      className={`${classes.paneScroll} ${overlayScrollClasses.overlayScroll}`}
    >
      <div className={classes.section}>
        <div className={classes.sectionTitle}>
          {t("modeling_inputs_v3.admin_actions_pane.section_title")}
        </div>

        <ActionCard
          icon={DeleteSweep}
          title={t("modeling_inputs_v3.admin_actions_pane.delete_title")}
          description={
            <Trans i18nKey="modeling_inputs_v3.admin_actions_pane.delete_description">
              Apaga o arquivo enviado, todas as notas e perguntas pendentes
              deste mês, cancela jobs em andamento e{" "}
              <strong>zera os cruzamentos contraparte</strong> que dependiam
              desse arquivo (eles voltam a rodar quando alguém reenviar). O
              input volta ao estado &quot;Não enviado&quot;.
            </Trans>
          }
          buttonLabel={t("modeling_inputs_v3.admin_actions_pane.delete_btn")}
          onConfirm={handleDelete}
          confirmMessage={t(
            "modeling_inputs_v3.admin_actions_pane.delete_confirm",
          )}
          disabled={!hasDelivered}
          disabledReason={t(
            "modeling_inputs_v3.admin_actions_pane.delete_disabled",
          )}
          busy={deleteBusy}
          result={deleteResult}
          classes={classes}
        />

        <ActionCard
          icon={Cached}
          title={t("modeling_inputs_v3.admin_actions_pane.reprocess_title")}
          description={
            <Trans i18nKey="modeling_inputs_v3.admin_actions_pane.reprocess_description">
              Re-executa o pipeline (parse + cruzamentos + blocos dependentes)
              usando o <strong>mesmo arquivo</strong> que já está no mês.
            </Trans>
          }
          buttonLabel={t("modeling_inputs_v3.admin_actions_pane.reprocess_btn")}
          onConfirm={handleReprocess}
          confirmMessage={t(
            "modeling_inputs_v3.admin_actions_pane.reprocess_confirm",
          )}
          disabled={!hasDelivered}
          disabledReason={t(
            "modeling_inputs_v3.admin_actions_pane.reprocess_disabled",
          )}
          busy={reprocessBusy}
          result={reprocessResult}
          classes={classes}
        />
      </div>
    </div>
  );
};

AdminActionsPane.propTypes = {
  input: PropTypes.object,
  appName: PropTypes.string,
  year: PropTypes.number,
  month: PropTypes.number,
  hasDelivered: PropTypes.bool,
  userRole: PropTypes.string,
  onReprocess: PropTypes.func,
};

export default AdminActionsPane;
